import { useState, useEffect } from "react";
import { Container, Form, FormControl, Row, Col, Button, Alert, Spinner } from "react-bootstrap";
import { collection, addDoc, serverTimestamp, query, where, onSnapshot, orderBy, updateDoc, doc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc as firestoreDoc, getDoc } from "firebase/firestore";
import { database } from "../Firebase";
import { Link } from "react-router-dom";

const sanitize = (str) => str?.trim().replace(/[<>{}[\]\\]/g, '') || '';

const validateForm = ({ nom, prenom, email, message }) => {
  if (!nom    || nom.trim().length    < 2) return "Le nom doit contenir au moins 2 caractères.";
  if (!prenom || prenom.trim().length < 2) return "Le prénom doit contenir au moins 2 caractères.";
  if (!email  || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Adresse email invalide.";
  if (!message || message.trim().length < 10) return "Le message doit contenir au moins 10 caractères.";
  if (message.trim().length > 1000) return "Le message ne peut pas dépasser 1000 caractères.";
  return null;
};

export default function Contact() {
  const [formData,       setFormData]       = useState({ nom: '', prenom: '', email: '', tel: '', message: '', sujet: '' });
  const [loading,        setLoading]        = useState(false);
  const [success,        setSuccess]        = useState(false);
  const [error,          setError]          = useState('');
  const [user,           setUser]           = useState(null);
  const [userData,       setUserData]       = useState(null);
  const [checkingAuth,   setCheckingAuth]   = useState(true);
  const [activeTab,      setActiveTab]      = useState('nouveau');
  const [mesMessages,    setMesMessages]    = useState([]);
  // ── State local pour le suivi des cases cochées ───────────────────────────
  const [reponsesVues,   setReponsesVues]   = useState({}); // { [msgId]: true }

  const auth = getAuth();

  // ── Auth + pré-remplissage ────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const snap = await getDoc(firestoreDoc(database, 'users', currentUser.uid));
          if (snap.exists()) {
            const data = snap.data();
            setUserData(data);
            setFormData(f => ({
              ...f,
              nom:    data.nom       || '',
              prenom: data.prenom    || '',
              email:  currentUser.email || '',
              tel:    data.telephone || '',
            }));
          }
        } catch (err) { console.warn("Erreur profil:", err); }
      } else {
        setUser(null);
        setUserData(null);
        setReponsesVues({});
      }
      setCheckingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  // ── Écoute temps réel des messages ───────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(database, 'messages_contact'),
      where('userId', '==', user.uid),
      orderBy('dateEnvoi', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMesMessages(msgs);
    });
    return () => unsub();
  }, [user]);

  // ── Marquer réponse comme vue — optimistic update ─────────────────────────
  const marquerReponseVue = async (msgId) => {
    // 1. Mise à jour locale IMMÉDIATE → la case se coche instantanément
    setReponsesVues(prev => ({ ...prev, [msgId]: true }));

    // 2. Sync Firestore en arrière-plan
    try {
      await updateDoc(doc(database, 'messages_contact', msgId), { reponseVue: true });
    } catch (e) {
      console.warn("marquerReponseVue:", e);
      // Rollback si erreur
      setReponsesVues(prev => {
        const copy = { ...prev };
        delete copy[msgId];
        return copy;
      });
    }
  };

  // ── Détermine si une réponse est vue (local OU Firestore) ─────────────────
  const isReponseVue = (msg) => reponsesVues[msg.id] === true || msg.reponseVue === true;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 1000) return;
    setFormData(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    const validationError = validateForm(formData);
    if (validationError) { setError(validationError); return; }
    setLoading(true); setError(''); setSuccess(false);
    try {
      await addDoc(collection(database, 'messages_contact'), {
        userId:       user.uid,
        nom:          sanitize(formData.nom),
        prenom:       sanitize(formData.prenom),
        email:        formData.email.trim().toLowerCase(),
        tel:          sanitize(formData.tel) || null,
        sujet:        formData.sujet || 'Non précisé',
        message:      sanitize(formData.message),
        statut:       'nouveau',
        reponseAdmin: null,
        reponseVue:   false,
        dateEnvoi:    serverTimestamp(),
      });
      setSuccess(true);
      setFormData(f => ({ ...f, sujet: '', message: '' }));
      setTimeout(() => setActiveTab('historique'), 1500);
    } catch (err) {
      console.error('Erreur Firestore:', err);
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally { setLoading(false); }
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  // ── Compteur basé sur state local + Firestore ─────────────────────────────
  const nbReponsesNonVues = mesMessages.filter(m =>
    m.reponseAdmin && !isReponseVue(m)
  ).length;

  // ── Chargement auth ───────────────────────────────────────────────────────
  if (checkingAuth) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" style={{ color: '#07a993' }} />
      </Container>
    );
  }

  // ── Non connecté ──────────────────────────────────────────────────────────
  if (!user) {
    return (
      <Container className="my-5 px-3 text-center" style={{ maxWidth: '480px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔒</div>
        <h2 style={{ fontSize: 'clamp(1.1rem, 4vw, 1.5rem)', fontWeight: 700, marginBottom: '12px' }}>
          Connexion requise
        </h2>
        <p style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '24px' }}>
          Pour nous contacter, vous devez être connecté à votre compte SpotyPark.
        </p>
        <div className="d-flex flex-column gap-3" style={{ maxWidth: '280px', margin: '0 auto' }}>
          <Link to="/login" className="btn btn-primary"
            style={{ fontWeight: 700, padding: '12px', borderRadius: '10px' }}>
            Se connecter
          </Link>
          <Link to="/inscription" className="btn btn-outline-success"
            style={{ fontWeight: 700, padding: '12px', borderRadius: '10px' }}>
            Créer un compte
          </Link>
        </div>
      </Container>
    );
  }

  return (
    <Container className="my-4 px-3" style={{ maxWidth: '680px' }}>

      {/* ── En-tête ─────────────────────────────────────────────────────── */}
      <div className="text-center mb-4">
        <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>✉️</div>
        <h2 style={{ fontSize: 'clamp(1.2rem, 4vw, 1.8rem)', fontWeight: 700, marginBottom: '8px' }}>
          Nous contacter
        </h2>
        <p style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: 0 }}>
          Nous répondons sous <strong>48h ouvrées</strong> directement dans l'application.
        </p>
      </div>

      {/* ── Bandeau utilisateur ──────────────────────────────────────────── */}
      <div className="d-flex align-items-center gap-2 p-3 rounded mb-4" style={{
        background: '#e8f8f5', border: '1px solid #07a993', fontSize: '0.85rem',
      }}>
        <span style={{ fontSize: '1.2rem' }}>👤</span>
        <div>
          <span style={{ fontWeight: 600, color: '#07a993' }}>
            {userData?.prenom || ''} {userData?.nom || ''}
          </span>
          <span style={{ color: '#6c757d', marginLeft: '8px' }}>{user.email}</span>
        </div>
        {userData?.estPionnier && (
          <span style={{
            marginLeft: 'auto', fontSize: '0.72rem',
            background: 'linear-gradient(135deg, #07a993, #0d6efd)',
            color: '#fff', padding: '2px 8px', borderRadius: '10px', fontWeight: 600,
          }}>
            🏅 Pionnier #{userData.numeroPionnier}
          </span>
        )}
      </div>

      {/* ── Onglets ──────────────────────────────────────────────────────── */}
      <div className="d-flex gap-2 mb-4">
        {[
          { key: 'nouveau', label: '✏️ Nouveau message' },
          {
            key: 'historique',
            label: (
              <span>
                📋 Mes messages ({mesMessages.length})
                {nbReponsesNonVues > 0 && (
                  <span style={{
                    fontSize: '0.62rem', background: '#07a993',
                    color: '#fff', padding: '2px 6px',
                    borderRadius: '10px', marginLeft: '6px', fontWeight: 700,
                  }}>
                    {nbReponsesNonVues} réponse{nbReponsesNonVues > 1 ? 's' : ''}
                  </span>
                )}
              </span>
            ),
          },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            style={{
              flex: 1, padding: '10px 8px', borderRadius: '8px',
              border: `2px solid ${activeTab === key ? '#07a993' : '#dee2e6'}`,
              background: activeTab === key ? '#e8f8f5' : '#fff',
              fontWeight: activeTab === key ? 700 : 400,
              cursor: 'pointer', fontSize: 'clamp(0.82rem, 3vw, 0.92rem)',
              transition: 'all 0.15s', minHeight: '44px',
            }}>
            {label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          ONGLET NOUVEAU MESSAGE
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'nouveau' && (
        <>
          {success && (
            <Alert variant="success" style={{ borderLeft: '4px solid #198754', fontSize: '0.88rem' }}>
              ✅ Message envoyé ! Vous recevrez une réponse directement dans l'application.
            </Alert>
          )}
          {error && (
            <Alert variant="danger" style={{ fontSize: '0.85rem', borderLeft: '4px solid #dc3545' }}>
              ❌ {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Row className="g-3 mb-3">
              <Col xs={12} md={6}>
                <Form.Group controlId="formNom">
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Nom *</Form.Label>
                  <FormControl type="text" name="nom" value={formData.nom}
                    onChange={handleChange} required style={{ fontSize: '16px' }} />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="formPrenom">
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Prénom *</Form.Label>
                  <FormControl type="text" name="prenom" value={formData.prenom}
                    onChange={handleChange} required style={{ fontSize: '16px' }} />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col xs={12} md={6}>
                <Form.Group controlId="formMail">
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Email *</Form.Label>
                  <FormControl type="email" name="email" value={formData.email} readOnly
                    style={{ fontSize: '16px', background: '#f8f9fa', color: '#6c757d', cursor: 'not-allowed' }} />
                  <Form.Text style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                    Email de votre compte — non modifiable
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="formTel">
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                    Téléphone <span className="text-muted fw-normal">(optionnel)</span>
                  </Form.Label>
                  <FormControl type="tel" name="tel" value={formData.tel}
                    onChange={handleChange} style={{ fontSize: '16px' }} placeholder="06 XX XX XX XX" />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group controlId="formSujet" className="mb-3">
              <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Sujet *</Form.Label>
              <Form.Select name="sujet" value={formData.sujet} onChange={handleChange}
                required style={{ fontSize: '16px' }}>
                <option value="">-- Choisissez un sujet --</option>
                <option value="probleme_technique">🔧 Problème technique</option>
                <option value="reservation">📅 Problème de réservation</option>
                <option value="paiement">💳 Problème de paiement</option>
                <option value="abonnement">⭐ Question sur l'abonnement</option>
                <option value="signalement">🚨 Signalement d'un utilisateur</option>
                <option value="parrainage">🎁 Programme de parrainage</option>
                <option value="suggestion">💡 Suggestion d'amélioration</option>
                <option value="autre">💬 Autre</option>
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="formMessage" className="mb-4">
              <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Message *</Form.Label>
              <FormControl as="textarea" rows={6} name="message" value={formData.message}
                onChange={handleChange} required
                style={{ fontSize: '16px', resize: 'none' }}
                placeholder="Décrivez votre demande en détail..." maxLength={1000} />
              <div className="d-flex justify-content-between mt-1">
                <Form.Text style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                  Minimum 10 caractères
                </Form.Text>
                <Form.Text style={{
                  fontSize: '0.72rem',
                  color:    formData.message.length > 900 ? '#dc3545' : '#6c757d',
                  fontWeight: formData.message.length > 900 ? 600 : 400,
                }}>
                  {formData.message.length}/1000
                </Form.Text>
              </div>
            </Form.Group>

            <Button type="submit" disabled={loading} className="w-100"
              style={{
                background: 'linear-gradient(135deg, #07a993, #0d6efd)',
                border: 'none', fontWeight: 700, padding: '14px',
                fontSize: '0.95rem', borderRadius: '10px',
                opacity: loading ? 0.75 : 1,
              }}>
              {loading
                ? <><Spinner animation="border" size="sm" className="me-2" />Envoi en cours...</>
                : '📩 Envoyer le message'}
            </Button>

            <p className="text-center text-muted mt-3" style={{ fontSize: '0.75rem' }}>
              * Champs obligatoires. Réponse sous 48h ouvrées directement dans l'app.
            </p>
          </Form>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          ONGLET HISTORIQUE + RÉPONSES
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'historique' && (
        <>
          {mesMessages.length === 0 ? (
            <div className="text-center py-5" style={{
              background: '#f8f9fa', borderRadius: '12px', border: '1px dashed #dee2e6',
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📭</div>
              <p style={{ color: '#6c757d', fontSize: '0.88rem', margin: 0 }}>
                Vous n'avez pas encore envoyé de message.
              </p>
              <button onClick={() => setActiveTab('nouveau')}
                style={{
                  marginTop: '16px', padding: '8px 20px', borderRadius: '8px',
                  background: '#07a993', color: '#fff', border: 'none',
                  fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
                }}>
                ✏️ Envoyer un message
              </button>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {mesMessages.map(msg => {
                const hasReponse    = !!msg.reponseAdmin;
                // ── Utilise le state local en priorité ────────────────────
                const reponseVue    = isReponseVue(msg);
                const reponseNonVue = hasReponse && !reponseVue;

                return (
                  <div key={msg.id} style={{
                    background:  '#fff',
                    border:      `1px solid ${reponseNonVue ? '#07a993' : '#dee2e6'}`,
                    borderLeft:  `4px solid ${reponseNonVue ? '#07a993' : hasReponse ? '#07a993' : '#dee2e6'}`,
                    borderRadius: '10px', padding: '14px',
                    boxShadow:   reponseNonVue ? '0 2px 8px rgba(7,169,147,0.15)' : 'none',
                    transition:  'all 0.2s',
                  }}>

                    {/* ── Header ─────────────────────────────────────────── */}
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: '2px' }}>
                          {msg.sujet || 'Sans sujet'}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          📅 {formatDate(msg.dateEnvoi)}
                        </div>
                      </div>
                      <div className="d-flex gap-2 align-items-center flex-wrap">
                        {reponseNonVue && (
                          <span style={{
                            fontSize: '0.65rem', background: '#07a993',
                            color: '#fff', padding: '3px 8px',
                            borderRadius: '10px', fontWeight: 700,
                          }}>
                            💬 Nouvelle réponse !
                          </span>
                        )}
                        {hasReponse && !reponseNonVue && (
                          <span style={{
                            fontSize: '0.65rem', background: '#e8f8f5',
                            color: '#07a993', padding: '3px 8px',
                            borderRadius: '10px', fontWeight: 600,
                            border: '1px solid #07a993',
                          }}>
                            ✅ Répondu
                          </span>
                        )}
                        {!hasReponse && (
                          <span style={{
                            fontSize: '0.65rem', background: '#fff8e8',
                            color: '#f39c12', padding: '3px 8px',
                            borderRadius: '10px', fontWeight: 600,
                            border: '1px solid #f39c12',
                          }}>
                            ⏳ En attente
                          </span>
                        )}
                      </div>
                    </div>

                    {/* ── Message envoyé ─────────────────────────────────── */}
                    <div style={{
                      background: '#f8f9fa', borderRadius: '8px',
                      padding: '10px 12px', fontSize: '0.85rem',
                      color: '#495057', lineHeight: 1.6,
                      marginBottom: hasReponse ? '12px' : 0,
                      whiteSpace: 'pre-wrap',
                    }}>
                      {msg.message}
                    </div>

                    {/* ── Réponse admin ──────────────────────────────────── */}
                    {hasReponse && (
                      <div style={{
                        background: '#e8f8f5', border: '1px solid #07a993',
                        borderRadius: '8px', padding: '12px',
                      }}>
                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
                          <span style={{ fontWeight: 700, color: '#07a993', fontSize: '0.88rem' }}>
                            💬 Réponse de SpotyPark
                          </span>
                          <span style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                            {formatDate(msg.dateReponseAdmin)}
                          </span>
                        </div>

                        <p style={{
                          fontSize: '0.88rem', color: '#212529',
                          marginBottom: '12px',
                          lineHeight: 1.6, whiteSpace: 'pre-wrap',
                        }}>
                          {msg.reponseAdmin}
                        </p>

                        {/* ── Case à cocher ─────────────────────────────── */}
                        {reponseNonVue ? (
                          <div
                            onClick={() => marquerReponseVue(msg.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '10px 12px', borderRadius: '8px',
                              background: '#fff', border: '2px solid #07a993',
                              cursor: 'pointer', userSelect: 'none',
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#e8f8f5'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                          >
                            {/* Checkbox vide */}
                            <div style={{
                              width: '22px', height: '22px', flexShrink: 0,
                              borderRadius: '6px', border: '2px solid #07a993',
                              background: '#fff',
                            }} />
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#07a993' }}>
                              Marquer comme lu
                            </span>
                          </div>
                        ) : (
                          /* ── Déjà lu ────────────────────────────────── */
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                          }}>
                            <div style={{
                              width: '22px', height: '22px', flexShrink: 0,
                              borderRadius: '6px', background: '#07a993',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <span style={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, lineHeight: 1 }}>
                                ✓
                              </span>
                            </div>
                            <span style={{ fontSize: '0.82rem', color: '#07a993', fontWeight: 600 }}>
                              Lu
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Container>
  );
}